/**
 * Trial Banner Component
 *
 * Full-width banner shown below the mobile header.
 * Displays trial status and upgrade CTA without cramping the header.
 *
 * @see docs/TRIAL_SPACES_SPEC.md
 */

import { useLocation } from 'react-router-dom'
import type { TrialStatus } from '@/types/session'

interface TrialBannerProps {
  trialStatus: TrialStatus | null
  isPaidAccount?: boolean
  onUpgradeClick?: () => void
}

export function TrialBanner({ trialStatus, isPaidAccount, onUpgradeClick }: TrialBannerProps) {
  const location = useLocation()

  // Hide banner when upgrade flow is active (user is on upgrade page)
  const params = new URLSearchParams(location.search)
  if (params.get('upgrade') === '1') {
    return null
  }

  if (!trialStatus) return null
  if (isPaidAccount) return null

  // Don't show banner if expired (they'll be on PlanGate)
  if (trialStatus.isExpired || (!trialStatus.isTrialActive && !trialStatus.isGracePeriod)) {
    return null
  }

  const isGrace = trialStatus.isGracePeriod
  const timeRemaining = isGrace
    ? `${trialStatus.hoursRemaining ?? 0}h left`
    : `${trialStatus.daysRemaining} day${trialStatus.daysRemaining !== 1 ? 's' : ''} left`

  return (
    <div
      className={`trial-banner ${isGrace ? 'trial-banner--grace' : 'trial-banner--active'}`}
    >
      <span className="trial-banner__text">
        {isGrace ? 'Read-only:' : 'Free plan:'} {timeRemaining}
      </span>
      {onUpgradeClick && (
        <button className="trial-banner__btn" onClick={onUpgradeClick}>
          Upgrade
        </button>
      )}
    </div>
  )
}
